/**
 * @fileoverview Save organisation details mutation hook.
 * @description Creates or replaces the organisation details (PUT /sales/organisation-details),
 * sending the `od1.` version the dialog loaded as If-Match (none for the first save). Super Admin
 * only and needs a recent MFA sign-in. Once saved, the saved record replaces what the read query
 * held (so a first save, which the read answered with a 409, shows as saved at once instead of
 * waiting for a refetch) and the sales queries are refreshed.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  SALES_CRM_QUERY_KEYS,
  SALES_QUERY_KEY,
} from '@/features/sales/constants/prospects-query-keys.constants'
import { saveOrganisationDetails } from '@/features/sales/services/organisation-details.service'

import type {
  OrganisationDetails,
  OrganisationDetailsInput,
} from '@/features/sales/types/my-page.types'

/** @description The key of the organisation save, so the dialog can tell one is in flight. */
export const ORGANISATION_SAVE_MUTATION_KEY = ['sales', 'organisation-save'] as const

/**
 * @description Saves the organisation details, then refreshes every sales query.
 * @param {() => void} [onSuccess] - Called after a success, even when the dialog has unmounted
 * since: it is an option of the mutation, not of a `mutate` call.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the details and the version
 * loaded, or null for the first save).
 */
export function useSaveOrganisationDetails(onSuccess?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ORGANISATION_SAVE_MUTATION_KEY,
    mutationFn: ({ input, version }: { input: OrganisationDetailsInput; version: string | null }) =>
      saveOrganisationDetails(input, version),
    /**
     * @description Puts the saved record in the read query, refreshes every sales query, then reports
     * the success.
     * @param {OrganisationDetails} saved - The saved details and their new version.
     */
    onSuccess: (saved: OrganisationDetails) => {
      queryClient.setQueryData(SALES_CRM_QUERY_KEYS.organisationDetails, saved)
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
      onSuccess?.()
    },
  })
}
